import { useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Blocks, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useMarketplace, useMarketplacePlugins } from "@/api/queries/plugins";
import { RemoveMarketplaceDialog } from "@/components/marketplace-actions/RemoveMarketplaceDialog";
import { SectionCard } from "@/components/SectionCard";
import { PropGrid } from "@/components/PropGrid";
import { BackLink, DetailHeader } from "@/components/DetailHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { marketplaceSourceLabel } from "@/components/marketplace-actions/sourceLabel";
import { CatalogSourceAlert, PluginStatusBadge } from "@/components/plugin-catalog/CatalogStatus";
import { BulkApproveBar } from "@/components/plugin-catalog/BulkApproveBar";
import { PluginDecisionCells, PluginDecisionHeads } from "@/components/plugin-catalog/PluginDecisionCells";
import { PluginCatalogMore, PluginCatalogToolbar, usePluginFilter } from "@/components/plugin-catalog/catalogFilter";
import { PendingSection } from "@/components/plugin-catalog/PendingSection";
import { RepoContentPanel } from "@/components/repo-content/RepoContentPanel";
import { useMarketplacePending } from "@/api/queries/plugins";
import { useManagedRepos } from "@/api/queries/gitContent";
import { CreatePluginDialog } from "@/components/scaffold-actions/CreatePluginDialog";
import { SetOwnerDialog, OwnerValue } from "@/components/ownership/SetOwnerDialog";
import { useSetMarketplaceOwner } from "@/api/mutations/ownership";
import { useUpdateMarketplace } from "@/api/mutations/plugins";
import { toast } from "sonner";

// #232 (UC-4): Marketplace-Detail — Plugins dieser Quelle + Remove (Cascade).
// #287: Die Plugin-Liste kommt LIVE aus der marketplace.json (kein Sync mehr).
export function PluginsDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useMarketplace(id);
  const plugins = useMarketplacePlugins(id);
  const navigate = useNavigate();
  const [removeOpen, setRemoveOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  // #292 (UC-2): Verantwortlichen setzen (owns-Kante, admin-only).
  const [ownerOpen, setOwnerOpen] = useState(false);
  const setOwner = useSetMarketplaceOwner();
  const updateMarketplace = useUpdateMarketplace(); // #294 (UC-1)
  // #290 (UC-4): VOR den early-returns (Rules of Hooks) — auch die Quellen-Tabelle wird
  // mit einem echten Marketplace dreistellig.
  const filter = usePluginFilter(plugins.data?.plugins ?? []);
  // #291 (UC-4): eingebettetes Content-Panel — Katalog-Pflege am Fach-Ort (Gleichstand
  // mit Vorlagen/Plugin-Detail). repoId ist die Server-Zusage aus dem pending-Read.
  const pending = useMarketplacePending(id);
  const repos = useManagedRepos();
  const panelRepo = pending.data?.repoId
    ? ((repos.data ?? []).find((r) => r.id === pending.data.repoId) ?? null)
    : null;
  // #293 (UC-3): Werkbank eingeklappt; Deep-Links (ref/file/path) öffnen sie automatisch.
  const [searchParams, setSearchParams] = useSearchParams();
  const [werkbankOpen, setWerkbankOpen] = useState(false);
  const deepLink = !!(searchParams.get("ref") || searchParams.get("file") || searchParams.get("path"));
  const werkbankSichtbar = werkbankOpen || deepLink;
  function toggleWerkbank() {
    if (!werkbankSichtbar) {
      setWerkbankOpen(true);
      return;
    }
    setWerkbankOpen(false);
    // ein Deep-Link hält sie sonst offen — beim bewussten Verbergen die Params räumen
    if (deepLink) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("ref");
          next.delete("file");
          next.delete("path");
          return next;
        },
        { replace: true },
      );
    }
  }

  if (isLoading) return <SectionCard testId="detail-loading"><p className="text-text3">Lädt …</p></SectionCard>;
  if (isError || !data) {
    return (
      <SectionCard testId="detail-error">
        <BackLink to="/plugins" label="Plugins" />
        <p className="mt-3 text-text3">Marketplace nicht gefunden.</p>
      </SectionCard>
    );
  }

  const pluginRows = plugins.data?.plugins ?? [];

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <DetailHeader
        backTo="/plugins"
        backLabel="Plugins"
        icon={Blocks}
        title={data.name}
        badge={<Badge variant={data.scope === "platform" ? "enrolled" : "default"}>{data.scope}</Badge>}
        sub={marketplaceSourceLabel(data.source)}
        actions={
          <>
            {/* #276 (UC-2): Eintragen geht nur in einen EIGENEN Katalog — in ein fremdes
                Repo haben wir kein Schreibrecht. Der Knopf wird deshalb nicht versteckt,
                sondern erklärt sich: ein fehlender Knopf lässt den Admin suchen. */}
            <Button
              size="sm"
              onClick={() => setCreateOpen(true)}
              disabled={data.origin === "external"}
              title={data.origin === "external" ? "Fremde Quelle — wir können dort nichts eintragen" : undefined}
              data-testid="action-scaffold-plugin"
            >
              <Plus className="h-4 w-4" /> Plugin anlegen
            </Button>
            <Button variant="outline" size="sm" onClick={() => void plugins.refetch()} disabled={plugins.isFetching}>
              <RefreshCw className="h-4 w-4" /> {plugins.isFetching ? "Lade …" : "Aktualisieren"}
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setRemoveOpen(true)}><Trash2 className="h-4 w-4" /> Entfernen</Button>
          </>
        }
      />

      {/* #291 (UC-2): die Freigabe-Arbeitsliste dieses Katalogs — VOR dem Katalog, denn
          hier landet der Admin direkt nach dem Anlegen (US1). */}
      <PendingSection marketplaceId={data.id} />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_340px]">
        <SectionCard icon={<Blocks className="h-[18px] w-[18px]" />} title="Plugins" description="Live aus der marketplace.json. Pro Agent freischaltbar (Agent-Detail → Plugins).">
          <CatalogSourceAlert sources={plugins.data?.sources} />
          <BulkApproveBar plugins={filter.alleGefiltert} />
          {plugins.isLoading && !plugins.data ? (
            <p className="text-[13px] text-text3">Lade …</p>
          ) : plugins.isError ? (
            <p className="text-[13px] text-danger">Katalog konnte nicht geladen werden: {(plugins.error as Error | undefined)?.message}</p>
          ) : pluginRows.length === 0 ? (
            <p className="text-[13px] text-text3">Keine Plugins in dieser marketplace.json.</p>
          ) : (
            <>
            <PluginCatalogToolbar filter={filter} />
            {filter.gesamt === 0 ? (
              <p className="text-[13px] text-text3">Kein Plugin passt zu Suche/Filter.</p>
            ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plugin</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Status</TableHead>
                  <PluginDecisionHeads />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filter.sichtbar.map((p) => (
                  <TableRow key={p.ref} data-testid={`mp-plugin-${p.name}`}>
                    {/* #290 (UC-3): der Name führt ins Plugin-Detail (EIN Ort für ein
                        Plugin) — bewusst nur der Name, nicht die ganze Zeile: die
                        Freigabe-Checkboxen daneben sind eigene Klickziele. */}
                    <TableCell>
                      <Link
                        to={`/plugins/marketplaces/${encodeURIComponent(p.marketplaceId)}/${encodeURIComponent(p.name)}`}
                        className="font-medium text-accent-text hover:underline"
                        data-testid={`mp-plugin-link-${p.name}`}
                      >
                        {p.name}
                      </Link>
                      {p.description && <div className="truncate text-[11.5px] text-text3" title={p.description}>{p.description}</div>}
                    </TableCell>
                    <TableCell><span className="mono text-[12px] text-text3">{p.version ?? "—"}</span></TableCell>
                    <TableCell><PluginStatusBadge status={p.status} /></TableCell>
                    <PluginDecisionCells p={p} />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            )}
            <PluginCatalogMore filter={filter} />
            </>
          )}
        </SectionCard>

        <PropGrid
          rows={[
            ["ID", <span className="mono text-[12px]">{data.id}</span>],
            ["Name", <span className="mono text-[12px]">{data.name}</span>],
            ["Quelle", <span className="mono text-[12px] break-all">{marketplaceSourceLabel(data.source)}</span>],
            ["Scope", data.scope],
            [
              "Verantwortlich",
              // #292 (UC-2): owns-Kante — Plugins dieses Katalogs ERBEN den Wert.
              <span className="flex items-center gap-2" data-testid="marketplace-owner">
                <OwnerValue ownerId={data.ownerId} />
                <button
                  className="text-[11.5px] text-accent-text hover:underline"
                  onClick={() => setOwnerOpen(true)}
                  data-testid="marketplace-owner-edit"
                >
                  ändern
                </button>
              </span>,
            ],
            [
              "Auto-Update",
              // #294 (UC-1): der eine nachträglich änderbare Auslieferungs-Schalter —
              // an = Agents dieser Quelle ziehen freigegebene Änderungen beim nächsten
              // Start; aus = Updates nur über den Update-Knopf (User/Admin entscheidet).
              <label className="flex cursor-pointer items-center gap-2 text-[12.5px]" data-testid="marketplace-autoupdate">
                <input
                  type="checkbox"
                  checked={data.autoUpdate}
                  disabled={updateMarketplace.isPending}
                  onChange={(e) => {
                    const neu = e.target.checked;
                    updateMarketplace.mutate(
                      { id: data.id, autoUpdate: neu },
                      {
                        onSuccess: () =>
                          toast.success(neu ? "Auto-Update aktiv — Agents folgen Freigaben beim nächsten Start." : "Auto-Update aus — Updates nur noch über den Update-Knopf."),
                        onError: (err) => toast.error(`Ändern fehlgeschlagen: ${(err as Error).message}`),
                      },
                    );
                  }}
                />
                {data.autoUpdate ? "an — Freigaben werden ausgeliefert" : "aus — Updates manuell"}
              </label>,
            ],
            ["Plugins", String(pluginRows.length)],
            ["Erstellt", new Date(data.createdAt).toLocaleString()],
          ]}
        />
      </div>

      {/* #293 (UC-3): die Werkbank ist SPEICHER-Sicht (Git) — standardmäßig eingeklappt.
          Fach-Entscheidungen fallen oben (Arbeitsliste, Plugin-Detail); wer Dateien von
          Hand ändern will, klappt auf. Deep-Links (ref/file/path in der URL) öffnen sie
          automatisch, damit geteilte Links weiter funktionieren. */}
      {panelRepo && (
        <SectionCard
          title="Werkbank (Dateien)"
          description="Direkter Blick in den Katalog-Speicher — für Datei-Änderungen von Hand. Zum Verwalten braucht es sie nicht."
          action={
            <Button variant="outline" size="sm" onClick={toggleWerkbank} data-testid="werkbank-toggle">
              {werkbankSichtbar ? "Verbergen" : "Anzeigen"}
            </Button>
          }
          testId="werkbank-section"
        >
          {werkbankSichtbar ? (
            <div id="repo-content-panel" className="flex h-[calc(100vh-320px)] min-h-[440px] flex-col gap-3">
              <RepoContentPanel key={panelRepo.id} repo={panelRepo} />
            </div>
          ) : (
            <p className="text-[12.5px] text-text3">Eingeklappt — „Anzeigen" öffnet die Datei-Ansicht.</p>
          )}
        </SectionCard>
      )}

      {createOpen && (
        <CreatePluginDialog
          open
          onOpenChange={setCreateOpen}
          marketplaceId={data.id}
          marketplaceName={data.name}
          onCreated={(_draftBranch, pluginName) => {
            // #292 (Arbens Live-Fund): nach dem Anlegen DIREKT ins Plugin-Detail — dort
            // stehen Badge, Freigeben/Verwerfen und das Panel auf dem Entwurfs-Ref. Das
            // Marketplace-Panel auf den Entwurf umzuschalten war der alte, doppelte Weg.
            // KEINE eigenen refetches: die Scaffold-Mutation invalidiert bereits.
            navigate(`/plugins/marketplaces/${encodeURIComponent(data.id)}/${encodeURIComponent(pluginName)}`);
          }}
        />
      )}

      {/* #290 (Review-Fund): geteilter Dialog (eine Fassung für Liste + Detail; die
          Detail-Kopie vergaß den Checkbox-Reset beim Schließen). */}
      <RemoveMarketplaceDialog
        marketplace={data}
        open={removeOpen}
        onOpenChange={setRemoveOpen}
        onRemoved={() => navigate("/plugins")}
      />

      {ownerOpen && (
        <SetOwnerDialog
          open
          onOpenChange={setOwnerOpen}
          artifactLabel={`den Marketplace "${data.name}"`}
          currentOwnerId={data.ownerId ?? null}
          isPending={setOwner.isPending}
          onSave={(ownerId) =>
            setOwner.mutate(
              { id: data.id, ownerId },
              {
                onSuccess: () => {
                  toast.success(ownerId ? "Verantwortlicher gesetzt" : "Verantwortung ans System zurückgegeben");
                  setOwnerOpen(false);
                },
                onError: (e) => toast.error(`Setzen fehlgeschlagen: ${(e as Error).message}`),
              },
            )
          }
        />
      )}
    </div>
  );
}
